import { api, throwApiError } from "../../api/api";

/** Criativos com a Eco-IA: o Gemini desenha a partir do criativo padrão; o Claude escreve e confere. */

export type CreativeFormat = "FEED" | "STORY";
export type CreativeKind = "NOVO" | "REFAZER" | "TEXTOS" | "AJUSTE" | "STORIES";

export type CreativeBenefit = { title: string; value: string | null; note: string | null };

export type CreativeContent = {
  headline: string;
  tagline: string;
  roleTitle: string;
  roleSubtitle: string | null;
  roleIcon: string;
  benefits: CreativeBenefit[];
  requirements: string[];
  schedule: string;
  locationLine1: string;
  locationLine2: string | null;
  ctaTitle: string;
  ctaMain: string;
  ctaSub: string | null;
};

export type CreativeReview = { approved: boolean; problems: string[]; summary: string; attempts: number } | null;

export type CreativesStatus = {
  imageEnabled: boolean;
  aiEnabled: boolean;
  imageModel: string;
  reference: { id: string; fileName: string; styleGuide: string | null; createdAt: string } | null;
};

export type CreativeListItem = {
  rootId: string;
  id: string;
  version: number;
  versions: number;
  format: CreativeFormat;
  kind: CreativeKind;
  title: string;
  review: CreativeReview;
  campaign: { id: string; name: string } | null;
  createdByName: string | null;
  updatedAt: string;
};

export type CreativeVersion = {
  id: string;
  version: number;
  parentId: string | null;
  kind: CreativeKind;
  kindLabel: string;
  format: CreativeFormat;
  content: CreativeContent;
  photoPrompt: string | null;
  instruction: string | null;
  review: CreativeReview;
  campaign: { id: string; name: string } | null;
  createdByName: string | null;
  createdAt: string;
};

export type CreativeDetail = { id: string; rootId: string; archived: boolean; versions: CreativeVersion[] };

export type GeneratedCreative = { id: string; rootId: string; version: number; review: CreativeReview; title: string };

export type ContentSuggestion = { content: CreativeContent; photoPrompt: string; explanation: string };

export const EMPTY_CONTENT: CreativeContent = {
  headline: "TEMOS VAGA",
  tagline: "VENHA FAZER PARTE DO NOSSO TIME!",
  roleTitle: "",
  roleSubtitle: null,
  roleIcon: "",
  benefits: [{ title: "", value: null, note: null }],
  requirements: [""],
  schedule: "",
  locationLine1: "",
  locationLine2: null,
  ctaTitle: "INTERESSADOS",
  ctaMain: "CANDIDATE-SE PELO LINK",
  ctaSub: "INSCRIÇÃO RÁPIDA PELO CELULAR",
};

/** Atalhos de ajuste na imagem: um clique preenche o pedido. */
export const ADJUST_SHORTCUTS = [
  "Troque a pessoa da foto por uma mulher.",
  "Deixe a foto mais clara, com luz de dia.",
  "Aumente o tamanho do cargo.",
  "Mude o cenário para dentro de uma loja.",
  "Deixe a pessoa mais sorridente.",
  "Remova qualquer texto pequeno na roupa.",
];

/** Gerar leva de 1 a 3 minutos (geração, conferência e, se preciso, correção). */
const GENERATE = { timeout: 300_000 };
const AI = { timeout: 180_000 };

const call = async <T>(fallback: string, fn: () => Promise<{ data: T }>): Promise<T> => {
  try {
    const { data } = await fn();
    return data;
  } catch (error) {
    return throwApiError(error, fallback);
  }
};

/** Limpa o que o formulário deixou vazio antes de mandar. */
export function cleanContent(c: CreativeContent): CreativeContent {
  const t = (v: string | null) => (v ?? "").trim();
  return {
    ...c,
    headline: t(c.headline),
    tagline: t(c.tagline),
    roleTitle: t(c.roleTitle),
    roleSubtitle: t(c.roleSubtitle) || null,
    roleIcon: t(c.roleIcon),
    benefits: c.benefits.filter((b) => t(b.title)).map((b) => ({ title: t(b.title), value: t(b.value) || null, note: t(b.note) || null })),
    requirements: c.requirements.map(t).filter(Boolean),
    schedule: t(c.schedule),
    locationLine1: t(c.locationLine1),
    locationLine2: t(c.locationLine2) || null,
    ctaTitle: t(c.ctaTitle),
    ctaMain: t(c.ctaMain),
    ctaSub: t(c.ctaSub) || null,
  };
}

export const getCreativesStatusApi = () => call<CreativesStatus>("Erro ao consultar os criativos.", () => api.get("/creatives/status"));

export const listCreativesApi = (campaignId?: string) =>
  call<CreativeListItem[]>("Erro ao listar os criativos.", () => api.get("/creatives", { params: campaignId ? { campaignId } : undefined }));

export const getCreativeApi = (id: string) => call<CreativeDetail>("Erro ao carregar o criativo.", () => api.get(`/creatives/${id}`));

export const getCreativeImageApi = (id: string) =>
  call<Blob>("Erro ao carregar a imagem.", () => api.get(`/creatives/${id}/image`, { responseType: "blob" }));

export const getReferenceImageApi = () =>
  call<Blob>("Erro ao carregar o criativo padrão.", () => api.get("/creatives/reference/image", { responseType: "blob" }));

export const setReferenceApi = (image: File) => {
  const form = new FormData();
  form.append("image", image);
  return call<CreativesStatus["reference"]>("Erro ao trocar o criativo padrão.", () =>
    api.post("/creatives/reference", form, { headers: { "Content-Type": "multipart/form-data" }, ...AI }),
  );
};

export const suggestContentApi = (payload: { campaignId?: string; brief?: string }) =>
  call<ContentSuggestion>("A Eco-IA não conseguiu escrever os textos.", () => api.post("/creatives/suggest-content", payload, AI));

export const createCreativeApi = (payload: { campaignId?: string; content: CreativeContent; photoPrompt: string; format: CreativeFormat }) =>
  call<GeneratedCreative>("Não foi possível gerar o criativo.", () => api.post("/creatives", payload, GENERATE));

export const changeTextsApi = (id: string, content: CreativeContent) =>
  call<GeneratedCreative>("Não foi possível trocar os textos.", () => api.post(`/creatives/${id}/texts`, { content }, GENERATE));

export const adjustCreativeApi = (id: string, instruction: string) =>
  call<GeneratedCreative>("Não foi possível ajustar a imagem.", () => api.post(`/creatives/${id}/adjust`, { instruction }, GENERATE));

export const storiesCreativeApi = (id: string) =>
  call<GeneratedCreative>("Não foi possível gerar a versão para Stories.", () => api.post(`/creatives/${id}/stories`, {}, GENERATE));

export const redoCreativeApi = (id: string, photoPrompt?: string) =>
  call<GeneratedCreative>("Não foi possível refazer o criativo.", () => api.post(`/creatives/${id}/redo`, { photoPrompt }, GENERATE));

export const archiveCreativeApi = (id: string) => call<{ rootId: string }>("Erro ao arquivar o criativo.", () => api.post(`/creatives/${id}/archive`));

export const addCreativeAsAdApi = (id: string, payload: { campaignId: string; primaryText: string; headline: string; description?: string }) =>
  call<{ id: string; campaignId: string; position: number }>("Erro ao incluir o anúncio.", () => api.post(`/creatives/${id}/use-as-ad`, payload));

export async function downloadCreative(id: string, fileName: string) {
  const blob = await getCreativeImageApi(id);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const creativeFileName = (title: string, version: number) =>
  `criativo-${title
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")}-v${version}.png`;
